import { autoinject, BindingEngine, computedFrom } from "aurelia-framework";
import { UserModel, UserPhoneNumberModel } from "../../../models/manage/user.models";
import { UnitOfWorkObserver } from "../../../utilities/unit-of-work-observer";
import { EntityTypeConfigurationModel, EntityTypeConfigurationHelper } from "../../../models/basics/basics.models";
import { EntityTypes } from "@rightship-tech/common-ui/models/basic.models";
import { Guid } from "../../../utilities/guid";
import { LookupClientService } from "../../../services/lookups/lookup-client.service";
import { LookupValueRequest, LookupValue } from "../../../models/lookups/lookup.models";
import { ValidationsClientService } from "../../../services/validations/validations-client.service";
import { NotificationClientService, NotificationType } from "../../../services/basics/notification-client.service";
import { I18N } from "aurelia-i18n";
import { UserClientService } from "../../../services/authentication/user-client.service";
import { DialogClientService } from "@rightship-tech/common-ui/services/basic/dialog-client.service";
import { ManageRightShipUsersClientService } from "../../../services/modules/righship-qi-manage-users.service";
import { QueriesClientService } from "../../../services/queries/queries-client.services";
import { Claims } from "../../../constants/authentication/claims";
import { UserClaimsService } from "../../../services/authentication/user-claims.service";
import { BulkChangeUserRoleRequest, PersistRightShipQiUserRequest, UserCompanyAssociation } from "../../../models/customers/customer.models";
import * as IntlTelInput from "intl-tel-input";
import { EntityFinderSearch } from "../../components/entity-finder/entity-finder-search";
import { ValidationController, ValidationRules } from "aurelia-validation";
import { CustomValidationRenderer } from "../../../client-validation-system/custom-validation-renderer";
import { NotifyPropertyChangedBase, Reflection } from "../../../utilities/reflection";

@autoinject()
export class ManagePersonDialog extends NotifyPropertyChangedBase {

    private _reloadData: () => void;
    private isRightshipUser: boolean;
    public _userInEditUow: UnitOfWorkObserver;

    public userInEdit: UserModel = null;
    public userId: number;
    public branchId: number;
    public preDefinedRoles: LookupValue[] = [];
    public customerId: number;
    public selectedBusinessUnitId: number;
    public myProfile: boolean;
    public allRoles: any[] = [];
    public selectedRole: any;
    public isUserMfaExcluded: boolean = false;

    public roleStep: boolean;
    public businessUnitStep: boolean;
    public userDetailStep: boolean;

    public businessUnits: LookupValue[];
    public phoneCodes: LookupValue[];
    public selectedBusinessUnitIds: number[] = [];
    public phoneNumberTypeId: number;

    public canAmmendVetRules: boolean = false;
    public canEditAuthorizedUserForAmendingVettingRules: boolean;
    public canUpdateUsersCompanyAssociations: boolean;
    public canManageLockedRoles: boolean;
    public banner: string;
    
    public userCompanyAssociations: UserCompanyAssociation[] = [];
    public companyEntityTypeConfigurationModel: EntityTypeConfigurationModel[];

    public userIds: number[] = [];
    @computedFrom(Reflection.getPropertyPath<ManagePersonDialog>(x => x.userIds))
    get isBulkChangeRole() {
        return this.userIds && this.userIds.length > 0;
    }

    constructor(
        private bindingEngine: BindingEngine,
        private lookupsClientService: LookupClientService,
        private manageUsersService: ManageRightShipUsersClientService,
        private validationsClientService: ValidationsClientService,
        private notificationService: NotificationClientService,
        private userClientService: UserClientService,
        private userClaimService: UserClaimsService,
        private dialogClientService: DialogClientService,
        private queriesClientService: QueriesClientService,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper,
        private validationController: ValidationController,
        private i18N: I18N) {
        super();
        this.canEditAuthorizedUserForAmendingVettingRules = userClaimService.hasPermission(Claims.CanEditAuthorizedUserForAmendingVettingRules);
        this.canUpdateUsersCompanyAssociations = userClaimService.hasPermission(Claims.CanUpdateUsersCompanyAssociations);
        this.validationController.addRenderer(new CustomValidationRenderer());
        this.companyEntityTypeConfigurationModel = this.entityTypeConfigurationHelper.getConfigurationByKey(["BasicCompanySearch"]);
        this.isRightshipUser = this.userClaimService.hasPermission(Claims.RightShipUser);
        this.canManageLockedRoles = this.userClaimService.hasPermission(Claims.CanManageLockedRoles);
    }

    activate(model: { userId: number, reloadData: () => void, branchId?: number, preDefinedRoles?: LookupValue[], customerId: number, myProfile: boolean, banner: string, userIds: number[] }) {
        this.userId = model.userId;
        this._reloadData = model.reloadData;
        this.branchId = model.branchId;
        this.preDefinedRoles = model.preDefinedRoles ? model.preDefinedRoles : [];
        this.customerId = model.customerId;
        this.myProfile = model.myProfile;
        this.banner = model.banner;

        this.queriesClientService.getData('odata/RightShipQiRoles?$filter=((branchId eq ' + model.branchId + ') and (isActive eq true))&$orderby=roleId').then(x => {
            this.allRoles = x.value.filter(v => !v.isLocked || (v.isLocked && this.canManageLockedRoles));
            this.initializeUser();
        });

        this.roleStep = true;

        if (this.customerId) {
            this.loadBusinessUnits();
        }
        this.loadPhoneCodes();

        ValidationRules
            .ensure<this, number>(p => p.selectedBusinessUnitId)
            .displayName('Business Unit')
            .required()
            .when(x => x.customerId > 0)
            .on(this);

        this.userIds = model.userIds;
    }

    detached() {
        if (this._userInEditUow) {
            this._userInEditUow.dispose();
        }
        if (this.telInput && this.telInput.destroy)
            this.telInput.destroy();
        this._userInEditUow = null;
        this.userInEdit = null;
    }

    initializeUser() {
        if (this.userId == null) return;

        const initializeUow = () => {
            if (this._userInEditUow) {
                this._userInEditUow.dispose();
            }
            this._userInEditUow = UnitOfWorkObserver.getTrackingInstance(this.bindingEngine, this.userInEdit);
            setTimeout(() => {
                if (!this.userInEdit.branchId) {
                    this.userInEdit.branchId = this.branchId ? this.branchId : this.userInEdit.branchId;
                }

                if (!this.userInEdit.userId) {
                    this.userInEdit.isActive = true;
                }
            });
        }

        if (this.userId > 0) {
            this.manageUsersService.getUser(this.userId).then(x => {
                this.userInEdit = x.user;
                this.userCompanyAssociations = x.userCompanyAssociations;
                this.isUserMfaExcluded = x.isUserMfaExcluded;

                this.canAmmendVetRules = x.businessUnit ? x.businessUnit.canAmmendVetRules : false;

                x.businessUnitUserPermissions.forEach(businessUnitUserPermission => {
                    this.selectedBusinessUnitIds.push(businessUnitUserPermission.businessUnitId);
                });

                if (this.userInEdit.roleUsers && this.userInEdit.roleUsers.length) {
                    this.selectedRole = this.allRoles.filter(x => x.roleId == this.userInEdit.roleUsers[0].roleId)[0];
                }

                if (x.businessUnit)
                    this.selectedBusinessUnitId = x.businessUnit.businessUnitId;

                initializeUow();
            })
        }
        else {
            this.userInEdit = {
                email: "",
                firstName: "",
                lastName: "",
                fullName: "",
                isActive: false,
                isManager: false,
                isDefault: false,
                managerId: 0,
                sequenceNumber: 0,
                userId: 0,
                jobDescriptionId: 0,
                idNumber: null,
                jobDescription: { jobDescriptionId: 0, name: "", isActive: true, isDefault: false, sequenceNumber: 0 },
                branchId: 0,
                nationalId: 0,
                guid: "",
                entityType: EntityTypes.User,
                identifier: Guid.generateGuid(),
                userPhoneNumbers: [],
                roleUsers: [],
                permissions: [],
                identificationDocumentIssuerCountryId: null
            };
            this.isUserMfaExcluded = false;

            initializeUow();
        }

        if (!this.selectedRole) this.selectedRole = this.allRoles[0];
    }

    @computedFrom(Reflection.getPropertyPath<ManagePersonDialog>(z => z.userInEdit.userPhoneNumbers),
        Reflection.getPropertyPath<ManagePersonDialog>(z => z.changes.userInEdit.userPhoneNumbers),
        Reflection.getPropertyPath<ManagePersonDialog>(z => z.userInEdit.userPhoneNumbers.length))
    get phoneNumber(): string {
        if (this.userInEdit.userPhoneNumbers && this.userInEdit.userPhoneNumbers.length > 0) {
            return this.userInEdit.userPhoneNumbers[0].phoneNumber;
        }
        return null;
    }

    set phoneNumber(value: string) {
        if (!this.userInEdit.userPhoneNumbers) this.userInEdit.userPhoneNumbers = [];

        if (!value && !this.phoneNumberCode) {
            //if both phone number and phone country code have been removed, the phone number needs to be removed from list
            //otherwise it tries to save with empty phone number
            this.userInEdit.userPhoneNumbers.splice(0, this.userInEdit.userPhoneNumbers.length);
        }
        else if (this.userInEdit.userPhoneNumbers.length == 0) {
            this.userInEdit.userPhoneNumbers.push(<UserPhoneNumberModel>{ identifier: Guid.generateGuid(), phoneNumber: value, phoneNumberCodeId: null, phoneNumberTypeId: this.phoneNumberTypeId, phoneSequence: 0 });
        }
        else {
            this.userInEdit.userPhoneNumbers[0].phoneNumber = value;
            this.onPropertyChanged<ManagePersonDialog>(x => x.userInEdit.userPhoneNumbers)
        }
    }

    get phoneNumberCode(): number {
        if (this.userInEdit.userPhoneNumbers && this.userInEdit.userPhoneNumbers.length > 0) {
            return this.userInEdit.userPhoneNumbers[0].phoneNumberCodeId;
        }
        return null;
    }

    set phoneNumberCode(value: number) {
        if (!this.userInEdit.userPhoneNumbers) this.userInEdit.userPhoneNumbers = [];

        if (!value && !this.phoneNumber) {
            //if both phone number and phone country code have been removed, the phone number needs to be removed from list
            //otherwise it tries to save with empty phone number code
            this.userInEdit.userPhoneNumbers.splice(0, this.userInEdit.userPhoneNumbers.length);
        }
        else if (this.userInEdit.userPhoneNumbers.length == 0) {
            this.userInEdit.userPhoneNumbers.push(<UserPhoneNumberModel>{ identifier: Guid.generateGuid(), phoneNumber: null, phoneNumberCodeId: value, phoneNumberTypeId: this.phoneNumberTypeId, phoneSequence: 0 });
        }
        else {
            this.userInEdit.userPhoneNumbers[0].phoneNumberCodeId = value;
            this.onPropertyChanged<ManagePersonDialog>(x => x.userInEdit.userPhoneNumbers)
        }
    }

    loadBusinessUnits() {
        const request = new LookupValueRequest();
        request.lookupValueQuery = "RS_BusinessUnits";
        request.parameters = this.buParams;

        this.lookupsClientService.getLookup(request, 'default').then(x => {
            this.businessUnits = x.activeValues;
        });
    }

    loadPhoneCodes() {
        const request = new LookupValueRequest();
        request.lookupValueQuery = "PhoneCodes";

        const countriesData = window["intlTelInputGlobals"].getCountryData().sort((a, b) => {
            if (a["priority"] > b["priority"]) return 1;
            if (a["priority"] < b["priority"]) return -1;
            return 0;
        });

        //add empty countycode to be able to deselect code
        if (countriesData.filter(x => x.iso2 == "emtpy" && x.name == "").length == 0) {
            const empty = {
                name: "",
                iso2: "emtpy",
                dialCode: ""
            }
            countriesData.unshift(empty);
        }

        this.lookupsClientService.getLookup(request, 'default').then(x => {
            this.phoneCodes = x.activeValues.map(v => {
                const country = countriesData.filter(c => ('+' + c.dialCode.trim()) == v.name.trim());
                if (country && country.length > 0) {
                    v.additionalData.country = country[0].iso2;
                }
                return v;
            });
        });
    }

    changeRoleUsers(selectedItems: LookupValue[]) {
        const roleUsers = this.userInEdit.roleUsers;
        const roleIdsToAdd = selectedItems.filter(x => x["isSelected"] && !roleUsers.some(y => y.roleId == x.id)).map(x => x.id);
        const rolesToRemove = selectedItems.filter(x => !x["isSelected"] && roleUsers.some(y => y.roleId == x.id)).map(x => x.id);

        rolesToRemove.forEach(x => {
            const roleToRemoveIndex = this.userInEdit.roleUsers.findIndex(ru => ru.roleId == x);
            if (roleToRemoveIndex > -1) {
                this.userInEdit.roleUsers.splice(roleToRemoveIndex, 1);
            }
        });

        roleIdsToAdd.forEach(x => {
            this.userInEdit.roleUsers.push({
                dbId: 0,
                insertDate: new Date().toJSON(),
                isDefault: this.userInEdit.roleUsers.length == 0,
                roleId: x,
                updateDate: new Date().toJSON(),
                userId: this.userInEdit.userId,
                entityType: EntityTypes.RoleUser,
                identifier: Guid.generateGuid(),
            });
        })
    }

    roleNext() {
        this.roleStep = false;
        if (this.customerId) {
            this.businessUnitStep = true;
        }
        else {
            this.businessUnitNext();
        }
    }

    telInput: IntlTelInput.Plugin = null

    businessUnitNext() {
        this.validationController.validate({
            object: this
        }).then(validation => {
            if (validation.valid) {
                this.businessUnitStep = false;
                this.userDetailStep = true;
                setTimeout(() => {
                    const input = document.querySelector("#phone-number");
                    this.telInput = IntlTelInput(input, {
                        hiddenInput: "full",
                        separateDialCode: true,
                        preferredCountries: ["gb", "us", "sg", "au"],
                        utilsScript: "https://cdn.jsdelivr.net/npm/intl-tel-input@16.0.3/build/js/utils.js"
                    });

                    input.removeEventListener("countrychange", this.countryChanged.bind(this));
                    input.addEventListener("countrychange", this.countryChanged.bind(this));
                    this.telInput.setCountry('');

                    if (this.phoneNumberCode && this.phoneNumberCode > 0) {
                        const currentCode = this.phoneCodes.filter(x => x.id == this.phoneNumberCode);
                        if (currentCode && currentCode.length > 0)
                            this.telInput.setCountry(currentCode[0].additionalData.country);
                    }
                });
            }
        })
    }

    countryChanged() {
        const countryData = this.telInput.getSelectedCountryData();
        if (countryData && countryData.iso2 == "emtpy" && countryData.name == "") {
            this.phoneNumberCode = null;
        }

        if (countryData && countryData.dialCode) {
            const codes = this.phoneCodes.filter(x => x.name.trim() == ('+' + countryData.dialCode.trim()));

            if (codes.length > 0 && codes[0] && codes[0].id > 0) {
                if (codes[0].id != this.phoneNumberCode) {
                    this.phoneNumberCode = codes[0].id;
                }
            }
            else console.error("Phone code not found!");
        }
    }

    businessUnitBack() {
        this.businessUnitStep = false;
        this.roleStep = true;
    }

    userDetailsBack() {
        this.userDetailStep = false;
        if (this.customerId) {
            this.businessUnitStep = true;
        }
        else this.roleStep = true;
    }

    hasAccessToBusinessUnit(businessUnit: LookupValue) {
        return this.selectedBusinessUnitIds.filter(x => x == businessUnit.id).length > 0;
    }

    onBusinessUnitSelected(businessUnit: LookupValue) {
        const selectedBusinessUnitIdIndex = this.selectedBusinessUnitIds.indexOf(businessUnit.id);

        if (selectedBusinessUnitIdIndex < 0) {
            this.selectedBusinessUnitIds.push(businessUnit.id);
        }
        else {
            this.selectedBusinessUnitIds.splice(selectedBusinessUnitIdIndex, 1);
        }
    }

    onRoleChecked(role) {
        this.selectedRole = role;
    }

    modifyCompanyAssociation(userCompanyAssociation: UserCompanyAssociation, entityFinderSearch: EntityFinderSearch) {
        userCompanyAssociation["isInEdit"] = true;
        entityFinderSearch.filter = userCompanyAssociation.companyName;
        userCompanyAssociation.companyId = 0;
        userCompanyAssociation.companyName = '';
        userCompanyAssociation.otherCompany = '';
    }

    public save(resendInvite: boolean = false) {
        return new Promise<void>((resolve, reject) => {


            const saveChanges = () => {
                const changes = this._userInEditUow.getChanges();
                const request: PersistRightShipQiUserRequest = {
                    changes: changes,
                    id: this.userInEdit.userId,
                    newPassword: null,
                    passwordConfirmation: null,
                    businessUnitId: this.selectedBusinessUnitId,
                    customerId: this.customerId,
                    accessableBusinessUnitIds: this.selectedBusinessUnitIds,
                    resendInvite: resendInvite,
                    canAmmendVetRules: this.canAmmendVetRules,
                    userCompanyAssociations: this.userCompanyAssociations,
                    isUserMfaExcluded: this.isUserMfaExcluded || false
                };
                this.manageUsersService.save(request).then(x => {
                    if (x.validationResult && x.validationResult.hasError) {
                        this.validationsClientService.showValidationErrors(x.validationResult);
                        reject();
                    }
                    else {
                        this.notificationService.show(this.i18N.tr('user-successfully-saved-text'), "", NotificationType.Success);
                        const userInEdit = this.userClientService.currentUser;
                        resolve();
                        this._reloadData();
                        this.dialogClientService.hide();
                        if (userInEdit.id == this.userInEdit.userId) {
                            this.userClientService.reloadUser();
                        }
                        else {
                            this._userInEditUow.clearChanges();
                        }
                    }
                }).catch(() => {
                    this.notificationService.show(this.i18N.tr('user-cannot-be-saved-text'), "", NotificationType.Error);
                    reject();
                });
            };

            if (!this.userInEdit.firstName && !this.userInEdit.lastName) {
                this.userInEdit.fullName = 'undefined';
            }
            else {
                this.userInEdit.fullName = this.userInEdit.firstName + ' ' + this.userInEdit.lastName;
            }

            const pushFunc = () => {
                this.userInEdit.roleUsers.push({
                    roleId: this.selectedRole.roleId,
                    isDefault: true,
                    insertDate: new Date().toJSON(),
                    updateDate: new Date().toJSON(),
                    identifier: Guid.generateGuid()
                });

                setTimeout(() => {
                    saveChanges();
                });
            }

            const handleRolesFunc = () => {

                const roleUsersToDelete = this.userInEdit.roleUsers.filter(x => x.roleId != this.selectedRole.roleId);

                roleUsersToDelete.forEach(x => {
                    const index = this.userInEdit.roleUsers.indexOf(x);
                    this.userInEdit.roleUsers.splice(index, 1);
                })

                if (this.userInEdit.roleUsers.filter(x => x.roleId == this.selectedRole.roleId).length == 0) {
                    setTimeout(() => {
                        pushFunc();
                    });
                }
                else {
                    saveChanges();
                }

            }

            setTimeout(() => {
                handleRolesFunc();
            });
        });
    }

    public bulkChangeUserRole() {
        return new Promise<void>((resolve, reject) => {


            const saveChanges = () => {
                const request: BulkChangeUserRoleRequest = {
                    userIds: this.userIds,
                    newRoleId: this.selectedRole.roleId
                }
                this.manageUsersService.bulkChangeRole(request).then(x => {
                    if (x.validationResult && x.validationResult.hasError) {
                        this.validationsClientService.showValidationErrors(x.validationResult);
                        reject();
                    }
                    else {
                        this.notificationService.show(this.i18N.tr('user-successfully-saved-text'), "", NotificationType.Success);
                        resolve();
                        this._reloadData();
                        this.dialogClientService.hide();
                    }
                }).catch(() => {
                    this.notificationService.show(this.i18N.tr('user-cannot-be-saved-text'), "", NotificationType.Error);
                    reject();
                })
            };

            setTimeout(() => {
                saveChanges();
            });
        });
    }

    cancel() {
        this.dialogClientService.hide();
    }

    @computedFrom("userClientService.currentUser.id", "customerId")
    get buParams() {
        const customerIdParam = this.customerId ? this.customerId.toString() : 'null';
        const userIdParam = this.isRightshipUser ? 'null' : this.userClientService.currentUser.id.toString();

        return customerIdParam + ',' + userIdParam;
    }

    searchCompanySelected(e: any, userCompanyAssociation: UserCompanyAssociation) {
        if (e.detail.selectedEntity) {
            userCompanyAssociation.otherCompany = null;
            userCompanyAssociation.companyId = e.detail.selectedEntity.id;
            userCompanyAssociation.companyName = e.detail.selectedEntity.shortCompanyName;
            userCompanyAssociation["isInEdit"] = false;
        }
    }
}
